import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Boxes,
  Building2,
  CircleDollarSign,
  ClipboardList,
  FileBarChart,
  FileSearch,
  Gauge,
  Landmark,
  MapPinned,
  PackageSearch,
  ReceiptText,
  Scale,
  Search,
  TrendingUp,
  Users,
  WalletCards,
  X,
} from 'lucide-react';
import {
  setItemMasterModalStatus,
} from '../utility/Redux/modal';
import { selectCompanyAddress, selectPrefix, selectUserType, seledctAlowedpathlist } from '../utility/Redux/profile';
import { hasPageAccess } from '../utility/pageAccess';
import { useProductAnnouncements } from '../utility/productAnnouncements';

const reportMeta = {
  '/Route_Alert_Report': { category: 'Operations', description: 'Review route alerts and exceptions that need attention.', icon: AlertTriangle },
  '/Item_Wise_Sale_Report': { category: 'Sales', description: 'Understand sales quantity and value for every item.', icon: BarChart3 },
  '/Station_outstanding': { category: 'Stations', description: 'See pending and outstanding amounts by station.', icon: WalletCards },
  '/item_Wise_issue_report': { category: 'Inventory', description: 'Track item quantities issued across stations and routes.', icon: Boxes },
  '/Party_Balance_Report': { category: 'Parties', description: 'Check the current balance for a selected party.', icon: CircleDollarSign },
  '/Party_Wise_Balance_Report': { category: 'Parties', description: 'Compare balances across all parties in one view.', icon: Users },
  '/non_purchasing_parties': { category: 'Parties', description: 'Find parties with no purchases in the selected period.', icon: FileSearch },
  '/Party_Ledger': { category: 'Parties', description: 'View a complete debit and credit transaction ledger.', icon: ReceiptText },
  '/salesMap': { category: 'Sales', description: 'Explore sales activity and coverage on the map.', icon: MapPinned },
  '/All_Stations_Balance_Report': { category: 'Stations', description: 'Compare the latest balances of all stations.', icon: Building2 },
  '/Station_day_end_reports': { category: 'Stations', description: 'Review station day-end activity and closing figures.', icon: ClipboardList },
  '/Difference_report': { category: 'Reconciliation', description: 'Identify differences between issue and collection values.', icon: Scale },
  '/Station_Balance_Mismatch': { category: 'Reconciliation', description: 'Investigate station balance mismatches date-wise.', icon: Gauge },
  '/Profit_report': { category: 'Finance', description: 'Analyse detailed profit across business transactions.', icon: TrendingUp },
  '/Profit_summary': { category: 'Finance', description: 'Get a quick overview of profit performance.', icon: Landmark },
  '/Flow_Transaction_Report_Sum': { category: 'RFID', description: 'See a concise summary of RFID flow transactions.', icon: FileBarChart },
  '/Flow_Transaction_Report': { category: 'RFID', description: 'Inspect individual RFID flow transaction records.', icon: PackageSearch },
};

const Reports = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const companyAddress = useSelector(selectCompanyAddress);
  const prefix = useSelector(selectPrefix);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const baseMasters = [
    {
      name: 'Route Alert Report',
      path: '/Route_Alert_Report',
      userType: 'MANAGER,ADMIN',
      onclick: () => {
        navigate('/Route_Alert_Report');
      },
    },
    {
      name: 'Item Wise Sale Report',
      path: '/Item_Wise_Sale_Report',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/Item_Wise_Sale_Report');
      },
    }, 
    {
      name: 'Station Outstanding',
      path: '/Station_outstanding',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/Station_outstanding');
      },
    },
    {
      name: 'Item Wise Issue Report',
      path: '/item_Wise_issue_report',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/item_Wise_issue_report');
      },
    },
    {
      name: 'Party Balance Report',
      path: '/Party_Balance_Report',
      userType: 'MANAGER,ADMIN,USER,STORE',
      onclick: () => {
        navigate('/Party_Balance_Report');
        dispatch(setItemMasterModalStatus(true));
      },
    },
    {
      name: 'Party Wise Balance Report',
      path: '/Party_Wise_Balance_Report',
      userType: 'MANAGER,ADMIN,USER,STORE',
      onclick: () => {
        navigate('/Party_Wise_Balance_Report');
      },
    },
    {
      name: 'Non-Purchase Parties',
      path: '/non_purchasing_parties',
      userType: 'MANAGER,ADMIN,USER,STORE',
      onclick: () => {
        navigate('/non_purchasing_parties');
        dispatch(setItemMasterModalStatus(true));
      },
    },
    {
      name: 'Party Ledger',
      path: '/Party_Ledger',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/Party_Ledger');
      },
    },
    {
      name: 'Sales Map',
      path: '/salesMap',
      userType: 'MANAGER,ADMIN',
      onclick: () => {
        navigate('/salesMap');
      },
    },
    {
      name: 'All Station Balance Report',
      path: '/All_Stations_Balance_Report',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/All_Stations_Balance_Report');
      },
    },
    {
      name: 'Station Day End Report',
      path: '/Station_day_end_reports',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/Station_day_end_reports');
      },
    },
    {
      name: 'Difference Report',
      path: '/Difference_report',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/Difference_report');
      },
    },
    {
      name: 'Station Balance Mismatch',
      path: '/Station_Balance_Mismatch',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/Station_Balance_Mismatch');
      },
    },
    {
      name: 'Profit Report',
      path: '/Profit_report',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/Profit_report');
      },
    },
    {
      name: 'Profit Summary',
      path: '/Profit_summary',
      userType: 'MANAGER,ADMIN,STORE',
      onclick: () => {
        navigate('/Profit_summary');
      },
    },
   
  ];
  //test
  const userType = useSelector(selectUserType);
  const announcements = useProductAnnouncements(prefix, userType);
  const allowedPages = useSelector(seledctAlowedpathlist);
  const extraMasters =
  companyAddress?.rfid_module ==="Yes"
    ? [
        {
          name: 'Flow Transaction Summary',
          path: '/Flow_Transaction_Report_Sum',
          userType: 'MANAGER,ADMIN,STORE',
          onclick: () => navigate('/Flow_Transaction_Report_Sum'),
        },
        {
          name: 'Flow Transaction Report',
          path: '/Flow_Transaction_Report',
          userType: 'MANAGER,ADMIN,STORE',
          onclick: () => navigate('/Flow_Transaction_Report'),
        },
      ]
      : [];
  const masters = [...baseMasters, ...extraMasters].map((report) => ({
    ...report,
    ...reportMeta[report.path],
  }));

  const accessibleReports = masters.filter(
    (report) => report.userType.includes(userType) && hasPageAccess(userType, allowedPages, report.path)
  );

  const categories = ['All', ...new Set(accessibleReports.map((report) => report.category).filter(Boolean))];

  const searchText = query.trim().toLowerCase();
  const filteredReports = accessibleReports.filter((report) => {
    const matchesCategory = activeCategory === 'All' || report.category === activeCategory;
    const matchesSearch = !searchText || `${report.name} ${report.description} ${report.category}`.toLowerCase().includes(searchText);
    return matchesCategory && matchesSearch;
  });
  const normalizePath = (path = '') => String(path).trim().replace(/\/+$/, '').toLowerCase();
  const newReportPaths = new Set(announcements.filter((item) => item.showNewBadge && item.targetPath).map((item) => normalizePath(item.targetPath)));

  const openReport = (report) => {
    if (report.onclick) report.onclick();
    else navigate(report.path);
  };

  return (
    <main className="h-full overflow-auto bg-slate-50 px-4 py-5 text-slate-900 md:px-7 md:py-7">
      <div className="mx-auto max-w-[1500px]">
        <section
          className="relative overflow-hidden rounded-3xl px-5 py-7 text-white shadow-xl md:px-9 md:py-9"
          style={{
            backgroundColor: '#0f172a',
            backgroundImage: 'radial-gradient(circle at 88% 10%, rgba(249,115,22,0.42), transparent 34%), linear-gradient(135deg, #020617 0%, #0f172a 55%, #431407 100%)',
          }}
        >
          <div className="absolute -right-16 -top-24 h-64 w-64 rounded-full bg-orange-500/20 blur-3xl" />
          <div className="absolute -bottom-20 left-1/3 h-44 w-44 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="relative flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-orange-100 backdrop-blur">
                <FileBarChart size={14} /> Reports Centre
              </div>
              <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Find the right report, faster.</h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 md:text-base">Search by name or choose a category. Each card explains what the report contains before you open it.</p>
            </div>
            <div className="flex gap-3">
              <div className="min-w-[120px] rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
                <p className="text-2xl font-bold">{accessibleReports.length}</p>
                <p className="text-xs text-slate-300">Available reports</p>
              </div>
              <div className="min-w-[120px] rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
                <p className="text-2xl font-bold">{Math.max(0, categories.length - 1)}</p>
                <p className="text-xs text-slate-300">Categories</p>
              </div>
            </div>
          </div>
        </section>

        <section className="sticky top-0 z-20 -mx-1 mt-5 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-sm backdrop-blur md:p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="relative min-w-0 flex-1 xl:max-w-md">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search reports…"
                aria-label="Search reports"
                className="h-11 w-full rounded-xl border border-slate-300 bg-slate-50 pl-11 pr-10 text-sm font-medium outline-none transition placeholder:text-slate-400 focus:border-orange-500 focus:bg-white focus:ring-4 focus:ring-orange-100"
              />
              {query ? <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"><X size={15} /></button> : null}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 xl:pb-0" aria-label="Report categories">
              {categories.map((category) => (
                <button
                  type="button"
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition ${activeCategory === category ? 'bg-orange text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mb-3 mt-6 flex items-end justify-between gap-4 px-1">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{activeCategory === 'All' ? 'All reports' : activeCategory}</h2>
            <p className="mt-0.5 text-xs text-slate-500">{filteredReports.length} {filteredReports.length === 1 ? 'report' : 'reports'} found</p>
          </div>
        </div>

        {filteredReports.length ? (
          <div className="grid grid-cols-1 gap-4 pb-8 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filteredReports.map((report) => {
              const Icon = report.icon || FileBarChart;
              return (
                <button
                  type="button"
                  key={report.path}
                  onClick={() => openReport(report)}
                  className="group flex min-h-[190px] flex-col rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition duration-200 hover:-translate-y-1 hover:border-orange-300 hover:shadow-xl hover:shadow-orange-100/70 focus:outline-none focus:ring-4 focus:ring-orange-100"
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-orange-600 transition group-hover:bg-orange group-hover:text-white"><Icon size={21} /></span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">{report.category}</span>
                  </div>
                  <h3 className="mt-5 text-base font-bold leading-5 text-slate-900 group-hover:text-orange-700">{report.name}</h3>
                  {newReportPaths.has(normalizePath(report.path)) ? <span className="mt-2 w-fit rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" style={{ backgroundColor: '#F97316' }}>New feature</span> : null}
                  <p className="mt-2 flex-1 text-sm leading-5 text-slate-500">{report.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-orange-600">Open report <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Search size={22} /></span>
            <h3 className="mt-4 font-bold text-slate-800">No reports found</h3>
            <p className="mt-1 text-sm text-slate-500">Try another search term or clear the selected category.</p>
            <button type="button" onClick={() => { setQuery(''); setActiveCategory('All'); }} className="mt-4 rounded-lg bg-orange px-4 py-2 text-sm font-semibold text-white">Show all reports</button>
          </div>
        )}
      </div>
    </main>
  );
};

export default Reports;
